import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Clock } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { CategoryGlyph } from "../components/Illustrations";
import ShareButtons from "../components/ShareButtons";
import NewsletterCta from "../components/NewsletterCta";
import { ARTICLES, CATEGORY_COLORS, RESOURCE_CATEGORIES } from "../data/content";

export default function ArticlePage() {
  const { slug } = useParams();
  const article = ARTICLES.find((a) => a.slug === slug);

  if (!article) {
    return (
      <section className="container-hub py-40 text-center" data-testid="article-not-found">
        <h1 className="font-display text-3xl font-semibold text-[color:var(--ink)]">Article not found</h1>
        <Link to="/resources" className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-[color:var(--invert-bg)] px-6 py-3 text-sm font-semibold text-white">
          <ArrowLeft className="h-4 w-4" /> Back to Resources
        </Link>
      </section>
    );
  }

  const color = CATEGORY_COLORS[article.category];
  const seed = RESOURCE_CATEGORIES.indexOf(article.category);
  const related = ARTICLES.filter((a) => a.slug !== article.slug && a.category === article.category).slice(0, 3);
  const fallbackRelated = related.length ? related : ARTICLES.filter((a) => a.slug !== article.slug).slice(0, 3);

  return (
    <>
      <Seo title={article.title} description={article.excerpt} path={`/resources/${article.slug}`} />
      <article className="relative pt-32 pb-24 lg:pt-40" data-testid="article-page">
        <div className="grid-lines absolute inset-0 -z-10 opacity-40" />
        <div className="container-hub">
          <Reveal>
            <Link to="/resources" className="inline-flex items-center gap-1.5 text-sm font-medium text-[color:var(--ink-soft)] hover:text-[color:var(--ink)]">
              <ArrowLeft className="h-4 w-4" /> All resources
            </Link>
          </Reveal>

          <div className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <Reveal delay={0.05}>
                <span className="inline-flex items-center rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em]"
                  style={{ color, backgroundColor: `color-mix(in srgb, ${color} 12%, transparent)` }}>
                  {article.category}
                </span>
                <h1 className="font-display mt-5 text-4xl font-semibold leading-[1.05] tracking-[-0.02em] text-[color:var(--ink)] sm:text-5xl">
                  {article.title}
                </h1>
                <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-[color:var(--ink-soft)]">
                  <span className="font-medium text-[color:var(--ink)]">{article.author}</span>
                  <span>·</span><span>{article.date}</span>
                  <span>·</span><span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{article.readingTime}</span>
                </div>
              </Reveal>

              {/* Custom hero graphic (no stock photography) */}
              <Reveal delay={0.1}>
                <div className="mt-8 flex h-56 items-center justify-center overflow-hidden rounded-3xl border border-[color:var(--hairline)]"
                  style={{ background: `linear-gradient(135deg, color-mix(in srgb, ${color} 16%, var(--surface)), var(--surface))` }}>
                  <CategoryGlyph color={color} seed={seed} className="h-32 w-32" />
                </div>
              </Reveal>

              <Reveal delay={0.12}>
                <div className="prose-hub mt-10 max-w-2xl space-y-6">
                  {article.body.map((p, i) => (
                    <p key={i} className="text-[17px] leading-[1.8] text-[color:var(--ink)]">{p}</p>
                  ))}
                </div>
              </Reveal>

              <div className="mt-10 flex items-center justify-between border-t border-[color:var(--hairline)] pt-6">
                <span className="text-sm text-[color:var(--ink-soft)]">Share this article</span>
                <ShareButtons title={article.title} testId="article-share" />
              </div>
            </div>

            {/* Sidebar: related */}
            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-28">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[color:var(--ink-soft)]">Related reading</p>
                <div className="mt-4 space-y-3">
                  {fallbackRelated.map((a) => (
                    <Link key={a.slug} to={`/resources/${a.slug}`} data-testid={`related-${a.slug}`}
                      className="hub-card group block rounded-2xl p-5 transition-transform duration-300 hover:-translate-y-0.5">
                      <span className="text-[11px] font-semibold uppercase tracking-[0.14em]" style={{ color: CATEGORY_COLORS[a.category] }}>{a.category}</span>
                      <h3 className="font-display mt-2 text-base font-semibold leading-snug tracking-tight text-[color:var(--ink)]">{a.title}</h3>
                      <span className="mt-3 inline-flex items-center gap-1 text-xs text-[color:var(--ink-soft)]">Read <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" /></span>
                    </Link>
                  ))}
                </div>
              </div>
            </aside>
          </div>

          <div className="mt-16">
            <NewsletterCta />
          </div>
        </div>
      </article>
    </>
  );
}
